import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'longestsub', label: 'Longest Palin. Substring' },
  { key: 'countsub', label: 'Palindromic Substrings' },
  { key: 'validpalin', label: 'Valid Palindrome' },
  { key: 'lps', label: 'Longest Palin. Subsequence' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

const BoxRow = ({ label, values, getBoxState }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--muted)', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', minHeight: 46 }}>
      {values.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--muted)', padding: '12px 0' }}>empty</span>}
      {values.map((v, i) => {
        const st = getBoxState ? getBoxState(i) : {};
        return (
          <div key={i} style={{
            width: 38, height: 38, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, fontFamily: "'Fira Code','Consolas',monospace",
            background: st.bg || 'var(--bg3)', color: st.color || 'var(--text)',
            border: `1.5px solid ${st.border || 'var(--border)'}`, transition: 'all 0.2s ease',
          }}>{v}</div>
        );
      })}
    </div>
  </div>
);

const GridBoxes = ({ label, grid, getCellState }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--muted)', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, overflowX: 'auto' }}>
      {grid.map((row, r) => (
        <div key={r} style={{ display: 'flex', gap: 4 }}>
          {row.map((cell, c) => {
            const st = getCellState ? getCellState(r, c) : {};
            return (
              <div key={c} style={{
                width: 34, height: 34, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                fontSize: 12, fontWeight: 800, fontFamily: "'Inter',sans-serif",
                background: st.bg || 'var(--bg3)', color: st.color || (cell === null || cell === undefined ? 'var(--muted)' : 'var(--text)'),
                border: `1.5px solid ${st.border || 'var(--border)'}`, transition: 'all 0.2s ease',
              }}>{cell === null || cell === undefined ? '·' : cell}</div>
            );
          })}
        </div>
      ))}
    </div>
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 200 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={{
      padding: '8px 14px', borderRadius: 9, cursor: step === 0 ? 'default' : 'pointer',
      border: `1.5px solid ${step === 0 ? 'var(--border)' : V1}`, background: 'transparent',
      color: step === 0 ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
      fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: step === 0 ? 0.6 : 1,
    }}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

/* ─── Longest Palindromic Substring ───────────────────────────────── */
const LongestSubMode = () => {
  const [input, setInput] = useState('babad');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const s = input.slice(0, 12);
    if (s.length === 0) return;
    const built = [];
    let bestStart = 0, bestLen = 0;
    const expand = (l, r) => {
      while (l >= 0 && r < s.length && s[l] === s[r]) { l--; r++; }
      return { l: l + 1, r: r - 1 };
    };
    for (let center = 0; center < s.length; center++) {
      const odd = expand(center, center);
      const oddLen = odd.r - odd.l + 1;
      if (oddLen > bestLen) { bestLen = oddLen; bestStart = odd.l; }
      built.push({ range: [odd.l, odd.r], best: [bestStart, bestStart + bestLen - 1], note: `Odd center at ${center}: expands to [${odd.l},${odd.r}] = "${s.slice(odd.l, odd.r + 1)}" (length ${oddLen}).` });
      if (center + 1 < s.length) {
        const even = expand(center, center + 1);
        const evenLen = even.r - even.l + 1;
        if (evenLen > bestLen) { bestLen = evenLen; bestStart = even.l; }
        built.push({ range: evenLen > 0 ? [even.l, even.r] : null, best: [bestStart, bestStart + bestLen - 1], note: evenLen > 0 ? `Even center between ${center},${center + 1}: expands to [${even.l},${even.r}] = "${s.slice(even.l, even.r + 1)}" (length ${evenLen}).` : `Even center between ${center},${center + 1}: no match (length 0).` });
      }
    }
    built.push({ done: true, best: [bestStart, bestStart + bestLen - 1], note: `Final longest palindromic substring: "${s.slice(bestStart, bestStart + bestLen)}".` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const s = input.slice(0, 12).split('');
  const getBoxState = i => {
    if (!cur) return {};
    if (cur.range && i >= cur.range[0] && i <= cur.range[1]) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    if (cur.done && cur.best && i >= cur.best[0] && i <= cur.best[1]) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff' };
    return {};
  };

  const code = {
    lines: [
      'String longestPalindrome(String s) {',
      '    int start = 0, maxLen = 0;',
      '    for (int c = 0; c < s.length(); c++) {',
      '        int len1 = expand(s, c, c);',
      '        int len2 = expand(s, c, c + 1);',
      '        int len = Math.max(len1, len2);',
      '        if (len > maxLen) {',
      '            maxLen = len;',
      '            start = c - (len - 1) / 2;',
      '        }',
      '    }',
      '    return s.substring(start, start + maxLen);',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 11 : 6,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="s" values={s} getBoxState={getBoxState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="babad (max 12 chars)" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a string (max 12 chars), then Run — expand around every possible center.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Longest Palindromic Substring" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Palindromic Substrings (Count) ──────────────────────────────── */
const CountSubMode = () => {
  const [input, setInput] = useState('aaa');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const s = input.slice(0, 10);
    if (s.length === 0) return;
    const built = [];
    let count = 0;
    for (let center = 0; center < s.length; center++) {
      let l = center, r = center, contributed = 0;
      while (l >= 0 && r < s.length && s[l] === s[r]) { count++; contributed++; l--; r++; }
      built.push({ range: [center, center], count, note: `Odd center at ${center}: found ${contributed} palindrome${contributed === 1 ? '' : 's'} expanding here. Running total: ${count}.` });
      l = center; r = center + 1; contributed = 0;
      while (l >= 0 && r < s.length && s[l] === s[r]) { count++; contributed++; l--; r++; }
      built.push({ range: center + 1 < s.length ? [center, center + 1] : null, count, note: center + 1 < s.length ? `Even center between ${center},${center + 1}: found ${contributed} palindrome${contributed === 1 ? '' : 's'} expanding here. Running total: ${count}.` : `No even center here (end of string). Running total: ${count}.` });
    }
    built.push({ done: true, count, note: `Final count of palindromic substrings: ${count}.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const s = input.slice(0, 10).split('');
  const getBoxState = i => (cur && cur.range && i >= cur.range[0] && i <= cur.range[1]) ? { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' } : {};

  const code = {
    lines: [
      'int countSubstrings(String s) {',
      '    int count = 0;',
      '    for (int c = 0; c < s.length(); c++) {',
      '        count += expandAndCount(s, c, c);',
      '        count += expandAndCount(s, c, c + 1);',
      '    }',
      '    return count;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? -1 : 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="s" values={s} getBoxState={getBoxState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="aaa (max 10 chars)" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a string (max 10 chars), then Run — every successful expansion is one more palindrome.` : cur.note}
          {cur && cur.count !== undefined && <> <Chip color={GREEN}>count = {cur.count}</Chip></>}
        </p>
      </div>
      <CodePanel title="Java — Palindromic Substrings (Count)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Valid Palindrome ────────────────────────────────────────────── */
const ValidPalinMode = () => {
  const [input, setInput] = useState('race a car');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const s = input.slice(0, 24);
    if (s.length === 0) return;
    const isAlnum = ch => /[a-zA-Z0-9]/.test(ch);
    const built = [];
    let left = 0, right = s.length - 1;
    let valid = true;
    while (left < right) {
      while (left < right && !isAlnum(s[left])) { built.push({ left, right, skip: 'left', note: `'${s[left]}' at index ${left} isn't alphanumeric — skip it.` }); left++; }
      while (left < right && !isAlnum(s[right])) { built.push({ left, right, skip: 'right', note: `'${s[right]}' at index ${right} isn't alphanumeric — skip it.` }); right--; }
      if (left < right) {
        const a = s[left].toLowerCase(), b = s[right].toLowerCase();
        if (a !== b) {
          built.push({ left, right, mismatch: true, note: `'${s[left]}' vs '${s[right]}' (case-insensitive) — mismatch! Not a palindrome.` });
          valid = false;
          break;
        }
        built.push({ left, right, note: `'${s[left]}' == '${s[right]}' (case-insensitive) — match. Move both pointers inward.` });
        left++; right--;
      }
    }
    built.push({ done: true, valid, note: valid ? `Pointers met without any mismatch — this IS a valid palindrome.` : `A mismatch was found — this is NOT a valid palindrome.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const s = input.slice(0, 24).split('');
  const getBoxState = i => {
    if (!cur || cur.done) return {};
    if (cur.left === i || cur.right === i) return { bg: cur.mismatch ? `linear-gradient(135deg,${RED},#c0293a)` : `linear-gradient(135deg,${V1},${V2})`, border: cur.mismatch ? RED : V1, color: '#fff' };
    return {};
  };

  const code = {
    lines: [
      'boolean isPalindrome(String s) {',
      '    int left = 0, right = s.length() - 1;',
      '    while (left < right) {',
      '        while (left < right && !isAlnum(s.charAt(left))) left++;',
      '        while (left < right && !isAlnum(s.charAt(right))) right--;',
      '        if (toLower(s.charAt(left)) != toLower(s.charAt(right))) return false;',
      '        left++; right--;',
      '    }',
      '    return true;',
      '}',
    ],
    active: !cur ? -1 : cur.mismatch ? 5 : cur.done ? -1 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="s" values={s} getBoxState={getBoxState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="race a car (max 24 chars)" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a string (max 24 chars, punctuation/spaces OK), then Run.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Valid Palindrome (Two Pointers)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Longest Palindromic Subsequence ─────────────────────────────── */
const LpsMode = () => {
  const [input, setInput] = useState('bbbab');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const s = input.slice(0, 8);
    if (s.length === 0) return;
    const n = s.length;
    const dp = Array.from({ length: n }, () => new Array(n).fill(null));
    const built = [];
    for (let i = n - 1; i >= 0; i--) {
      dp[i][i] = 1;
      built.push({ grid: dp.map(r => [...r]), cell: [i, i], note: `dp[${i}][${i}] = 1 — a single character is always a palindrome of length 1.` });
      for (let j = i + 1; j < n; j++) {
        if (s[i] === s[j]) {
          const inner = (i + 1 <= j - 1 && dp[i + 1][j - 1] !== null) ? dp[i + 1][j - 1] : 0;
          dp[i][j] = inner + 2;
          built.push({ grid: dp.map(r => [...r]), cell: [i, j], note: `s[${i}]='${s[i]}' == s[${j}]='${s[j]}' — match! dp[${i}][${j}] = dp[${i + 1}][${j - 1}] + 2 = ${inner} + 2 = ${dp[i][j]}.` });
        } else {
          const a = dp[i + 1][j] ?? 0, b = dp[i][j - 1] ?? 0;
          dp[i][j] = Math.max(a, b);
          built.push({ grid: dp.map(r => [...r]), cell: [i, j], note: `s[${i}]='${s[i]}' ≠ s[${j}]='${s[j]}' — dp[${i}][${j}] = max(dp[${i + 1}][${j}], dp[${i}][${j - 1}]) = max(${a}, ${b}) = ${dp[i][j]}.` });
        }
      }
    }
    built.push({ grid: dp.map(r => [...r]), done: true, note: `Final answer: dp[0][${n - 1}] = ${dp[0][n - 1]}.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const s = input.slice(0, 8);
  const emptyGrid = Array.from({ length: s.length }, () => new Array(s.length).fill(null));
  const getCellState = (r, c) => (cur && cur.cell && cur.cell[0] === r && cur.cell[1] === c) ? { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' } : {};

  const code = {
    lines: [
      'int longestPalindromeSubseq(String s) {',
      '    int n = s.length();',
      '    int[][] dp = new int[n][n];',
      '    for (int i = n - 1; i >= 0; i--) {',
      '        dp[i][i] = 1;',
      '        for (int j = i + 1; j < n; j++) {',
      '            if (s.charAt(i) == s.charAt(j)) {',
      '                dp[i][j] = dp[i + 1][j - 1] + 2;',
      '            } else {',
      '                dp[i][j] = Math.max(dp[i + 1][j], dp[i][j - 1]);',
      '            }',
      '        }',
      '    }',
      '    return dp[0][n - 1];',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 13 : cur.note.includes('match!') ? 7 : cur.note.includes('single character') ? 4 : 9,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GridBoxes label={`dp grid — longest palindromic subsequence in s[i..j]`} grid={cur ? cur.grid : emptyGrid} getCellState={getCellState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="bbbab (max 8 chars)" />
          <button onClick={run} style={smallBtn(V1)}>Build Table</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a string (max 8 chars), then Build Table — filled bottom-up by increasing substring length.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Longest Palindromic Subsequence (2-D DP)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const words = ['level', 'radar', 'hello', 'world', 'civic', 'apple', 'noon', 'grape'];
  const w = words[Math.floor(Math.random() * words.length)];
  const isPalin = w === [...w].reverse().join('');
  return { w, correct: isPalin ? 'yes' : 'no' };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const options = [
    { key: 'yes', label: 'Yes — it reads the same backward' },
    { key: 'no', label: 'No — it does not' },
  ];

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '4px 0 14px', lineHeight: 1.6 }}>
        Is <span style={{ fontFamily: 'monospace', color: V2 }}>"{scenario.w}"</span> a palindrome?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const PalindromesPlayground = () => {
  const [mode, setMode] = useState('longestsub');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'longestsub' && <LongestSubMode />}
      {mode === 'countsub' && <CountSubMode />}
      {mode === 'validpalin' && <ValidPalinMode />}
      {mode === 'lps' && <LpsMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default PalindromesPlayground;
